import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { Div, Dropdown, Flex, Gutter, Input, OptionListOption } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Address, Person } from '@cdaas/api-clients/dist/asset-user-openapi';

import { COUNTRY_OPTIONS, VERIFY_POSTAL_CODE } from '../../utils/constants';
import { RootState } from '../../store/reducers';
import { UserGroupEditForm } from '../../Pages/Onboarding/GroupSettings/GroupSettings';

type UserGroupEditFieldsProps = {
  mode: 'billing' | 'delivery';
  formUtils: Omit<UseFormReturn<UserGroupEditForm>, 'handleSubmit'>;
};

const UserGroupEditFields: FunctionComponent<UserGroupEditFieldsProps> = ({ mode, formUtils }) => {
  const { t } = useTranslation('globalComponents');
  const emptyCountryOption = { label: '', value: '' };
  const [selectedCountry, setSelectedCountry] = useState<OptionListOption>();
  const selectedUserGroup = useSelector((state: RootState) => state.userGroups.selectedUserGroup);

  const countryOptions: OptionListOption[] = COUNTRY_OPTIONS.map(countryOption => ({
    label: countryOption.label ? t(`countries.${countryOption.label}`) : countryOption.label,
    value: countryOption.value,
  }));

  const watchCountry = formUtils.watch(`${mode}.country`);

  useEffect(() => {
    const addressKey = `default_${mode}_address` as 'default_billing_address' | 'default_delivery_address';

    const newCountry = countryOptions.find(co => co.value === selectedUserGroup?.[addressKey]?.country);
    if (newCountry) {
      setSelectedCountry({ label: newCountry.label, value: newCountry.value });
    } else {
      setSelectedCountry(emptyCountryOption);
    }
    // constants skipped as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserGroup, mode]);

  useEffect(() => {
    if (!watchCountry) {
      // A trick to get not empty country to `dropdown` and `form`, if not exist in backend
      formUtils.setValue(`${mode}.country`, emptyCountryOption.value, { shouldDirty: false });
      setSelectedCountry({
        label: '',
        value: '',
      });
    } else {
      setSelectedCountry({
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        label: countryOptions.find(cc => cc.value === watchCountry)?.label!,
        value: watchCountry,
      });
    }
    // constants skipped as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchCountry, mode]);

  const handleCountryChange = (_e: SyntheticEvent, option: OptionListOption): void => {
    if (!option) return;
    formUtils.setValue(`${mode}.country`, option.value, { shouldDirty: true });
    formUtils.clearErrors(`${mode}.country`);
  };

  const getFormsInputErrorMessage = (inputName: keyof Omit<Address, 'name'> | keyof Person): string | undefined => {
    const errors = formUtils.formState.errors[mode] as Partial<Record<keyof Omit<Address, 'name'> | keyof Person, { message: string }>>;
    return errors?.[inputName]?.message;
  };

  return (
    <Div>
      <Flex>
        <Controller
          name={`${mode}.street`}
          control={formUtils.control}
          render={({ field: { onChange, value } }) => (
            <Input
              label={_.capitalize(t('UserGroupEdit.streetAddress'))}
              value={value}
              onChange={onChange}
              errorText={getFormsInputErrorMessage('street')}
            />
          )}
          defaultValue=""
        />
      </Flex>

      <Gutter />

      <Flex>
        <Controller
          name={`${mode}.address1`}
          control={formUtils.control}
          render={({ field: { onChange, value } }) => (
            <Input label={_.capitalize(t('UserGroupEdit.address1'))} value={value === null ? undefined : value} onChange={onChange} />
          )}
          defaultValue=""
        />
      </Flex>

      <Gutter />

      <Flex>
        <Controller
          name={`${mode}.address2`}
          control={formUtils.control}
          render={({ field: { onChange, value } }) => (
            <Input label={_.capitalize(t('UserGroupEdit.address2'))} value={value === null ? undefined : value} onChange={onChange} />
          )}
          defaultValue=""
        />
      </Flex>

      <Gutter />

      <Flex>
        <Controller
          name={`${mode}.city`}
          control={formUtils.control}
          render={({ field: { onChange, value } }) => (
            <Input label={_.capitalize(t('UserGroupEdit.city'))} value={value} onChange={onChange} errorText={getFormsInputErrorMessage('city')} />
          )}
          defaultValue=""
        />
        <Div margin={{ left: 'sm' }}>
          <Controller
            name={`${mode}.zip`}
            control={formUtils.control}
            render={({ field: { onChange, value } }) => (
              <Input label={_.capitalize(t('UserGroupEdit.zip'))} value={value} onChange={onChange} errorText={getFormsInputErrorMessage('zip')} />
            )}
            rules={{
              pattern: {
                value: VERIFY_POSTAL_CODE,
                message: `${t('UserGroupEdit.errorTextPleaseEnter')} ${t('UserGroupEdit.zip')}`,
              },
            }}
            defaultValue=""
          />
        </Div>
      </Flex>

      <Gutter />

      <Flex>
        <Dropdown
          label={_.capitalize(t('UserGroupEdit.country'))}
          options={countryOptions}
          onChange={handleCountryChange}
          selectedOption={selectedCountry}
          errorText={getFormsInputErrorMessage('country')}
        />
      </Flex>
    </Div>
  );
};

export default UserGroupEditFields;
